<template>
    <simple-layout>
        <basic-card>
            <template slot="header">{{$t('processing.importDefinitions')}}</template>
            <data-table
                    :configuration="importDefinitionTableConfiguration"
                    :data="importDefinitionsByType"
                    @focusedChanged="handleFocusedChanged"
                    @add="handleAdd"
                    @delete="handleDelete"
                    @execute="handleExecute"
                    component-id="import-definitions-list"
            >
                <basic-card v-if="focusedDefinition != null">
                    <template slot="header">{{$t('editation')}} {{focusedDefinition.displayName}}</template>
                    <form-wrapper>
                        <form-text-input :caption="`${$t('displayName')}`" v-model="focusedDefinition.displayName"/>
                        <form-text-input :caption="`${$t('processing.icon')}`" v-model="focusedDefinition.icon"/>
                        <form-input-layout>
                          <template slot="caption">
                              {{ $t('processing.iconPreview') }}
                          </template>
                          <template slot="input">
                            <icon :icon="['fal', focusedDefinition.icon]" size="2x"/>
                          </template>
                        </form-input-layout>
                        <form-text-input :caption="`${$t('description')}`" v-model="focusedDefinition.description"/>
                        <form-select-input :caption="`${$t('type')}`" v-model="focusedDefinition.type"
                                           :options="scriptTypeOptions"/>
                        <template v-if="['EVENT_TRIGGER', 'DEFAULTS'].includes(focusedDefinition.type)">
                            <form-select-input :caption="`${$t('processing.triggerObjects')}`" :options="triggerObjectsOptions" v-model="focusedDefinition.triggerObjectType"/>
                            <form-multi-select-input :caption="`${$t('processing.triggerActions')}`" :options="triggerActionsOptions" v-model="focusedDefinition.triggerActionTypes"/>
                            <template v-if="focusedDefinition.triggerObjectType == 'operation'">
                              <form-multi-select-input :caption="`${$t('processing.operationDefinitions')}`" :options="operationDefinitionOptions" v-model="focusedDefinition.operationDefinitions"/>
                            </template>
                        </template>
                    </form-wrapper>
                    Script: <br/>
                    <div style="min-height: 500px;">
                        <ace-editor v-model="focusedDefinition.script" :key="focusedDefinition.systemName"/>
                    </div>
                    <br/>
                    <simple-button @press="save">{{$t("save")}}</simple-button>
                </basic-card>
            </data-table>
        </basic-card>
    </simple-layout>
</template>

<script>
    import BasicCard from "../components/layout/BasicCard";
    import SimpleLayout from "../components/layout/SimpleLayout";
    import {mapGetters} from "vuex";
    import DataTable from "../components/table/DataTable";
    import FormWrapper from "../components/form/FormWrapper";
    import FormTextInput from "../components/form/FormTextInput";
    import SimpleButton from "../components/button/SimpleButton";
    import AceEditor from "../components/scripting/AceEditor";
    import FormSelectInput from "../components/form/FormSelectInput";
    import Axios from "axios";
    import FormMultiSelectInput from "../components/form/FormMultiSelectInput";
    import FormInputLayout from "@/components/form/FormInputLayout";

    export default {
        name: "ScriptDefinitionsView",
        components: {
          FormInputLayout,
            FormMultiSelectInput,
            FormSelectInput,
            AceEditor, SimpleButton, FormTextInput, FormWrapper, DataTable, SimpleLayout, BasicCard
        },
        data: function () {
            return {
                triggerObjectsOptions: [{id: 'operation', name: 'Operation'}],
                triggerActionsOptions: [{id: 'CREATED', name: 'On create'}, {id: 'UPDATED', name: 'On update'}, {id: 'DELETED', name: 'On delete'},
                  {id: 'REQUESTED', name: "On demand"}],
                focusedDefinition: null,
                importDefinitionTableConfiguration: {
                    enableAdd: true,
                    allowEdit: true,
                    columns: [
                        {
                            caption:this.$t('displayName'),
                            datum: d => d.displayName
                        },
                        {
                            caption: this.$t('description'),
                            datum: d => d.description
                        },
                        {
                            caption: this.$t('type'),
                            datum: d => d.type
                        }
                    ],
                    actions: [
                        {
                            name: "delete",
                            icon: "trash",
                            tooltip: this.$t('processing.deleteScript'),
                        },
                        {
                            name: "execute",
                            icon: "cogs",
                            accessLambda: d => d.type == 'JOB',
                            tooltip: this.$t('processing.executeScript'),
                        }
                    ]
                },
                scriptTypeOptions: [{name: 'File import', id: 'FILE_IMPORT'}, {name: 'Job', id: 'JOB'},
                    {name: 'Event trigger', id: "EVENT_TRIGGER"}, {name: 'Defaults', id: "DEFAULTS"}],
            }
        },
        methods: {
            save() {
                let promise = this.$store.dispatch("importDefinitions/saveImportDefinition", this.focusedDefinition).then(() => {
                    this.$notify({
                        group: 'application',
                        title: this.$t('processing.saveSuccessTitle'),
                        text:   this.$t('processing.saveSuccessText', {defName : this.focusedDefinition.systemName}) ,
                        type: "ok"
                    });
                })
                this.focusedDefinition = null;
                return promise;
                //this.$store.dispatch("importDefinitions/refreshImportDefinitions");
            },
            handleFocusedChanged(focused) {
                this.focusedDefinition = focused;
            },
            handleAdd() {
                this.focusedDefinition = {script: ""}
            },
            handleExecute(def) {
                return Axios.post("processing/api/v1/processes", {
                    values: {},
                    definitionId: def.id,
                }).then(res => {
                    if (res.status === 201) {
                        this.$notify({
                            group: 'application',
                            title: `Execution of script started`,
                            text: `Execution of ${def.displayName} started`,
                            type: "ok"
                        })
                    } else if (res.status === 409) {
                        this.$notify({
                            group: 'application',
                            title: 'Execution failed',
                            text: `Execution of ${def.displayName} failed`,
                            type: "error"
                        })
                    }
                });
            },
            handleDelete(def) {
                this.$store.dispatch("importDefinitions/deleteImportDefinition", def)
                    .then(this.refreshData)
            },
            refreshData() {
                this.$store.dispatch("importDefinitions/refreshImportDefinitions");
            }
        },
        computed: {
            ...mapGetters({
                importDefinitions: "importDefinitions/importDefinitions",
                operationDefinitions: "operationDefinitions/operationDefinitions"
            }),
            operationDefinitionOptions() {
              return this.operationDefinitions.map(def => ({id: def.id, name: def.name ?? "N/A"}));
            },
            importDefinitionsByType() {
                return this.importDefinitions
            }
        },
        mounted() {
            this.refreshData();
        }
    }
</script>

<style lang="scss" scoped>

</style>
